import React from "react";
import { useTranslation } from "react-i18next";

/**
 * 6-axis hexagonal radar — Company Health Radar (Section 4 Dashboard V3).
 *
 * Consumes the array returned by /api/dashboard/health-radar (6 axes :
 * contracts, legal, insurance, financing, compliance, hr).
 *
 * Each axis carries score 0-100 + status ∈ {strong,ok,weak,critical,unmeasured}.
 * Unmeasured axes still render the spoke but the polygon vertex stays at the
 * very center (score 0 visually = "no signal yet").
 *
 * Visual : white card with light grid (3 rings + 6 spokes) + filled polygon
 * (Electric Blue at 18 % opacity) + outline (Electric Blue) + axis label
 * dots colored per status.
 */
const RADAR_AXES = ["contracts", "legal", "insurance", "financing", "compliance", "hr"];

const STATUS_COLOR = {
  strong:     "#22C55E",
  ok:         "#1677FF",
  weak:       "#FBBF24",
  critical:   "#FF6B7A",
  unmeasured: "#94A3B8",
};

function polarPoint(cx, cy, radius, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
}

export default function HealthRadarChart({ axes = [], size = 380, testId = "health-radar-chart" }) {
  const { t } = useTranslation("dashboard");
  // Add lateral padding so axis labels (e.g. "Conformité") never get clipped
  // by the viewBox. The actual hex stays centered & sized.
  const padX = 56;
  const padY = 20;
  const cx = size / 2 + padX;
  const cy = size / 2 + padY;
  const ringRadius = size / 2 - 30;
  const labelRadius = ringRadius + 24;
  const viewW = size + 2 * padX;
  const viewH = size + 2 * padY;

  // Build a map for quick lookup; fall back to unmeasured if missing.
  const byAxis = Object.fromEntries((axes || []).map((a) => [a.axis, a]));

  // 6 spokes evenly spaced — first axis at the top, then clockwise.
  const spokes = RADAR_AXES.map((axisKey, i) => {
    const angle = (i / RADAR_AXES.length) * 360;
    const a = byAxis[axisKey] || { score: 0, status: "unmeasured", measured: false };
    const [x, y] = polarPoint(cx, cy, (a.score / 100) * ringRadius, angle);
    const [lx, ly] = polarPoint(cx, cy, labelRadius, angle);
    return { axisKey, angle, a, x, y, lx, ly };
  });

  const polygonPoints = spokes.map((s) => `${s.x},${s.y}`).join(" ");

  return (
    <div
      data-testid={testId}
      className="bg-white border border-zinc-200 rounded-2xl p-6 flex flex-col items-center"
    >
      <header className="self-start mb-2">
        <p className="qenva-kicker !text-qenva-green">{t("v3.radar.kicker")}</p>
        <h3 className="font-display font-bold text-qenva-navy text-[18px] mt-2 tracking-tight">
          {t("v3.radar.title")}
        </h3>
        <p className="text-[12.5px] text-zinc-500 mt-0.5 max-w-md">
          {t("v3.radar.subtitle")}
        </p>
      </header>

      <svg width="100%" viewBox={`0 0 ${viewW} ${viewH}`} style={{ maxWidth: viewW, display: "block", height: "auto" }} role="img" aria-label="QENVA Health Radar">
        {/* concentric rings */}
        {[0.33, 0.66, 1].map((r, i) => {
          const pts = RADAR_AXES.map((_, j) => {
            const angle = (j / RADAR_AXES.length) * 360;
            const [px, py] = polarPoint(cx, cy, ringRadius * r, angle);
            return `${px},${py}`;
          }).join(" ");
          return (
            <polygon
              key={i}
              points={pts}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="1"
              strokeDasharray={i === 2 ? "0" : "2 4"}
            />
          );
        })}

        {/* spokes */}
        {spokes.map((s) => (
          <line
            key={`spoke-${s.axisKey}`}
            x1={cx}
            y1={cy}
            x2={polarPoint(cx, cy, ringRadius, s.angle)[0]}
            y2={polarPoint(cx, cy, ringRadius, s.angle)[1]}
            stroke="#E2E8F0"
            strokeWidth="1"
          />
        ))}

        {/* data polygon — filled */}
        <polygon
          points={polygonPoints}
          fill="rgba(22,119,255,0.18)"
          stroke="#1677FF"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* vertex dots colored per status */}
        {spokes.map((s) => (
          <circle
            key={`vertex-${s.axisKey}`}
            cx={s.x}
            cy={s.y}
            r={s.a.measured ? 5 : 3}
            fill={STATUS_COLOR[s.a.status] || STATUS_COLOR.unmeasured}
            stroke="white"
            strokeWidth="2"
          />
        ))}

        {/* axis labels */}
        {spokes.map((s) => {
          // Pick text anchor based on position
          let anchor = "middle";
          if (s.lx < cx - 6) anchor = "end";
          else if (s.lx > cx + 6) anchor = "start";
          const labelY = s.ly + (s.angle === 0 ? -2 : s.angle === 180 ? 14 : 4);
          return (
            <g key={`label-${s.axisKey}`}>
              <text
                x={s.lx}
                y={labelY}
                fontSize="11"
                fontWeight="600"
                fill="#0B2341"
                textAnchor={anchor}
              >
                {t(`v3.radar.axis.${s.axisKey}`)}
              </text>
              <text
                x={s.lx}
                y={labelY + 12}
                fontSize="10"
                fontWeight="500"
                fill={STATUS_COLOR[s.a.status]}
                textAnchor={anchor}
              >
                {s.a.measured ? `${s.a.score}` : "—"}
              </text>
            </g>
          );
        })}
      </svg>

      {/* legend */}
      <ul className="flex flex-wrap items-center gap-3 mt-4 text-[11px] text-zinc-500" data-testid="health-radar-legend">
        {["strong", "ok", "weak", "critical", "unmeasured"].map((s) => (
          <li key={s} className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLOR[s] }} />
            {t(`v3.radar.status.${s}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}
